/**
 * Which supporting-connection tiles to render on the digital-account step.
 *
 * Extracted from `ApplicantFlow` so the rule is testable on its own.
 *
 * **The invariant: never offer a tile the applicant cannot complete.**
 *
 * ⚠️ There used to be a SECOND filter here, `hideUnsatisfiable`, which dropped
 * manual-submit (`liveProvider`) tiles on a REQUIRED step — that path wrote no
 * workflow step, so the applicant would connect, see "connected", and be
 * blocked at Submit with nothing explaining why. Platform #442 retired the
 * manual path entirely, so the filter had no tile left to match and is gone.
 *
 * The invariant above is what survives, and it is the thing to re-read before
 * adding a tile that reaches the backend by some route other than OAuth — see
 * platform #522 (Instagram / X / TikTok) and #523.
 */

/** The structural slice of `SourceOption` this rule reads. */
export interface OfferableSource {
  /** Server-side OAuth path. Writes `oauth_<provider>`. */
  liveOAuth?: string;
  /** Render only when the backend advertises `liveOAuth` as available. */
  gatedOnAvailability?: boolean;
  /**
   * Opt-in: do not offer this tile on a REQUIRED definition step whose
   * `satisfied_by` does not name `oauth_<liveOAuth>`. Connecting it would not
   * complete the step, and the applicant would still be held at Submit.
   * Narrow by construction: no tile has it unless it asks, and it has no
   * effect when no required step is passed (the legacy path, an optional step).
   */
  hideWhenRequiredStepCannotUseIt?: boolean;
}

export interface OfferableContext {
  /**
   * `available_oauth_providers` from the session: the providers this
   * environment holds credentials for.
   */
  availableOAuth: readonly string[];
  /**
   * The `satisfied_by` of the definition's digital-account step, passed ONLY
   * when the plan is definition-driven and that step is required. Null or
   * absent means there is no required step to satisfy, so nothing is hidden on
   * its account.
   */
  requiredStepSatisfiedBy?: readonly string[] | null;
}

/**
 * ⚠️ A `gatedOnAvailability` tile with no `liveOAuth` is dropped rather than
 * shown. It is a half-configured tile — the gate has nothing to check — and
 * failing to the *hidden* side keeps the invariant this function exists for:
 * never offer something that cannot be completed.
 */
export function offerableSources<T extends OfferableSource>(
  sources: readonly T[],
  ctx: OfferableContext,
): T[] {
  return sources.filter((src) => {
    if (src.gatedOnAvailability) {
      if (!src.liveOAuth || !ctx.availableOAuth.includes(src.liveOAuth)) return false;
    }
    if (src.hideWhenRequiredStepCannotUseIt && ctx.requiredStepSatisfiedBy) {
      return !!src.liveOAuth && ctx.requiredStepSatisfiedBy.includes(`oauth_${src.liveOAuth}`);
    }
    return true;
  });
}
